import {cloneElement} from 'react'
import getColumnValue from './getColumnValue.mjs'
import makeColumnRowNumber from './makeColumnRowNumber.mjs'
import makeColumnSelection from './makeColumnSelection.jsx'
import ButtonConfig from './ButtonConfig/ButtonConfig.jsx'
import ColumnTitle from './ColumnTitle/ColumnTitle.jsx'

import {
    FilterBoolean,
    FilterDate,
    FilterDict,
    FilterNumber,
    FilterText,
    FilterLongText,
    Image,
    ImagePicker,
    InputBoolean,
    InputDate,
    InputDict,
    InputLongText,
    InputNumber,
    InputText,
    ViewBoolean,
    ViewDate,
    ViewDict,
    ViewLongText,
    ViewNumber,
    ViewText,
} from '.'

const dataTypeToInput = {
    boolean: <InputBoolean />,
    date: <InputDate />,
    dict: <InputDict />,
    image: <ImagePicker />,
    longtext: <InputLongText />,
    number: <InputNumber />,
    text: <InputText />,
}

const dataTypeToFilter = {
    boolean: <FilterBoolean />,
    date: <FilterDate />,
    dict: <FilterDict />,
    longtext: <FilterLongText />,
    number: <FilterNumber />,
    text: <FilterText />,
}

const dataTypeToViewer = {
    boolean: <ViewBoolean />,
    date: <ViewDate />,
    dict: <ViewDict />,
    image: <Image />,
    longtext: <ViewLongText />,
    number: <ViewNumber />,
    text: <ViewText />,
}

const getColumnUpdates = (value, dataIndex) => {
    if ('string' === typeof dataIndex) {
        return {[dataIndex]: value}
    }

    if (Array.isArray(dataIndex)) {
        const updates = {}
        let cursor = updates

        for (const key of dataIndex.slice(0, -1)) {
            cursor[key] = {}
            cursor = cursor[key]
        }

        cursor[dataIndex.at(-1)] = value
        return updates
    }
}

const enhanceColumnFilter = ({
    column,
    dataType,
    filter = dataTypeToFilter[dataType],
    getValue,
    table,
}) => {
    if (! filter) {
        return
    }

    const {dataIndex} = column

    const setFilter = (filter) => {
        table.setFilter(dataIndex, filter, getValue)
    }

    const setSort = (sort) => {
        table.setSort(dataIndex, sort, getValue)
    }

    const getValues = () => {
        const {filters, rows} = table
        const key = Array.isArray(dataIndex) ? dataIndex.join('.') : dataIndex
        const otherFilters = filters.filter(([k]) => k !== key)

        return Array.from(
            new Set(
                Array.from(table.iterRows(rows))
                    .filter(
                        (row) => otherFilters.every(
                            ([, filter, getValue]) => {
                                const value = getValue(row)
                                return filter(value)
                            }
                        )
                    )
                    .map(getValue)
            )
        )
    }

    column.filterDropdown = ({confirm}) => {
        return cloneElement(filter, {
            confirm,
            getValues,
            setFilter,
            setSort,
        })
    }

    // 控制过滤图标亮/灭
    column.filtered = table.hasFilter(dataIndex) || table.hasSort(dataIndex)

    // 设为受控模式，否则过滤图标不会变化
    column.filteredValue = false
}

const enhanceColumnRender = ({
    column,
    dataType,
    table,

    component = table.editable ?
        dataTypeToInput[dataType]
        :
        dataTypeToViewer[dataType],

    getUpdates,
    getValue,
    render,
}) => {
    if (render) {
        column.render = (dummy, row, index) => {
            const value = getValue(row)
            return render(value, row, index, table)
        }
    }
    else if (component) {
        column.render = (dummy, row, index) => {
            const value = getValue(row)

            const onChange = async (value) => {
                const key = row[table.rowKey]
                const updates = await getUpdates(value, row, table.rows)
                table.updateRows([[key, updates]])
            }

            const element = 'function' === typeof component ?
                component(value, row, index, table)
                :
                component

            if (element) {
                const props = {}

                if (! Object.hasOwn(element.props, 'value')) {
                    props.value = value
                }

                if (! Object.hasOwn(element.props, 'onChange')) {
                    props.onChange = onChange
                }

                return cloneElement(element, props)
            }
        }
    }
    else {
        column.render = (dummy, row) => {
            return getValue(row)
        }
    }
}

const enhanceColumn = (
    table,

    {
        component,
        dataIndex,
        dataType,
        filter,
        getValue = (row) => getColumnValue(row, dataIndex),
        getUpdates = (value, row, rows) => getColumnUpdates(value, dataIndex),
        key = dataIndex,
        render,
        title,
        ...column
    }
) => {
    Object.assign(column, {
        dataIndex,
        key,

        title: table.configurable ?
            <ColumnTitle columnKey={key}>{title}</ColumnTitle>
            :
            title,
    })

    enhanceColumnFilter({
        column,
        dataType,
        filter,
        getValue,
        table,
    })

    enhanceColumnRender({
        column,
        component,
        dataType,
        getUpdates,
        getValue,
        render,
        table,
    })

    return column
}

const enhanceColumns = ({
    noRowNumber,
    rowHeight,
    rowNumberTitle,
    rowSelection,
    table,
}) => {
    const extraCols = []

    if (rowSelection && rowHeight) {
        const colSelection = makeColumnSelection({rowSelection, table})
        extraCols.push(colSelection)
    }

    if (! noRowNumber) {
        const title = (() => {
            if (rowNumberTitle) {
                return rowNumberTitle
            }
            else if (table.configurable) {
                return <ButtonConfig />
            }
            else {
                return null
            }
        })()

        const colRowNumber = {
            ...makeColumnRowNumber(table),
            title,
        }

        extraCols.push(colRowNumber)
    }

    return [
        ...extraCols,

        ...table.refinedColumns.map(
            column => enhanceColumn(table, column)
        ),
    ]
}

export default enhanceColumns
